import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  attribute,
  cameraPosition,
  normalWorld,
  positionLocal,
  positionWorld,
  uniform,
  uv,
  wgslFn,
} from 'three/tsl';
import type { DerivedLighting } from './LightingSolver';
import {
  DEFAULT_STYLE_CONFIG,
  type CloudLayerConfig,
  type CloudLayerName,
  type CloudShape,
  type StyleConfig,
} from './StyleConfig';
import { SHARED_AERIAL_UNIFORMS, aerialPerspectiveFragmentExpression } from './AerialPerspective';
import { tagValueBucket, valueExposureUniform } from './ValueComposition';
import type { RendererMode } from './Renderer';

type CloudMaterial = THREE.ShaderMaterial | MeshBasicNodeMaterial;

export interface CloudField<M extends CloudMaterial = THREE.ShaderMaterial> {
  readonly group: THREE.Group;
  readonly material: M;
  applyStyle(style: StyleConfig): void;
  update(dt: number): void;
  dispose(): void;
}

const LAYER_NAMES: readonly CloudLayerName[] = ['low', 'mid', 'high'] as const;
const SHAPE_CODES: Record<CloudShape, number> = {
  ribbon: 0,
  wisp: 1,
  puff: 2,
  bank: 3,
};

interface LayerHandle {
  readonly name: CloudLayerName;
  readonly group: THREE.Group;
  readonly material: CloudMaterial;
  lastApplied: CloudLayerConfig;
  config: CloudLayerConfig;
}

interface CloudShaderMaterialOptions {
  cloudBaseHex: string;
  wrap: number;
  rimStrength: number;
}

type MaybeResolvedStyle = StyleConfig & { derivedLighting?: DerivedLighting };

const makeNodeAttribute = attribute as any;
const makeNodeUniform = uniform as any;
const makeNodeUv = uv as any;
const makeWgslFn = wgslFn as any;

interface WebGPUCloudUniforms {
  uSunDir: { value: THREE.Vector3 };
  uSunColor: { value: THREE.Color };
  uCloudZenithColor: { value: THREE.Color };
  uCloudHorizonColor: { value: THREE.Color };
  uCloudBase: { value: THREE.Color };
  uWrap: { value: number };
  uRimStrength: { value: number };
  uCloudFogDensity: { value: number };
  uSunDirWorld: { value: THREE.Vector3 };
  uHorizonColor: { value: THREE.Color };
  uZenithColor: { value: THREE.Color };
  uFogWarmTint: { value: THREE.Color };
  uFogCoolTint: { value: THREE.Color };
  uInscatterStrength: { value: number };
  uInscatterPower: { value: number };
  uValueExposure: { value: number };
}

function snapshotLayer(layer: CloudLayerConfig): CloudLayerConfig {
  return JSON.parse(JSON.stringify(layer)) as CloudLayerConfig;
}

export function buildCloudField(style?: StyleConfig, rendererMode?: 'webgl2'): CloudField<THREE.ShaderMaterial>;
export function buildCloudField(style: StyleConfig | undefined, rendererMode: 'webgpu'): CloudField<CloudMaterial>;
export function buildCloudField(style: StyleConfig | undefined, rendererMode: RendererMode): CloudField<CloudMaterial>;
export function buildCloudField(style: StyleConfig = DEFAULT_STYLE_CONFIG, rendererMode: RendererMode = 'webgl2'): CloudField<CloudMaterial> {
  const root = new THREE.Group();
  root.name = 'stylized-cloud-field';
  tagValueBucket(root, 'bg');

  const sharedMaterial = makeCloudShaderMaterial({
    cloudBaseHex: style.clouds.shading.cloudBaseHex,
    wrap: style.clouds.shading.wrap,
    rimStrength: style.clouds.shading.rimStrength,
  }, rendererMode);
  applyCloudFogStyle(sharedMaterial, style);

  const resolved = style as MaybeResolvedStyle;
  if (resolved.derivedLighting) {
    applyCloudUniforms(
      sharedMaterial,
      resolved.derivedLighting,
      style.clouds.shading.cloudBaseHex,
      style.clouds.shading.wrap,
      style.clouds.shading.rimStrength,
    );
  }

  const layers: LayerHandle[] = LAYER_NAMES.map((name) => {
    const group = new THREE.Group();
    group.name = `cloud-layer-${name}`;
    tagValueBucket(group, 'bg');
    root.add(group);
    const config = style.clouds.layers[name];
    return { name, group, material: sharedMaterial, config, lastApplied: snapshotLayer(config) };
  });

  for (const layer of layers) rebuildLayer(layer);

  return {
    group: root,
    material: sharedMaterial,
    applyStyle(nextStyle) {
      const nextResolved = nextStyle as MaybeResolvedStyle;
      if (nextResolved.derivedLighting) {
        applyCloudUniforms(
          sharedMaterial,
          nextResolved.derivedLighting,
          nextStyle.clouds.shading.cloudBaseHex,
          nextStyle.clouds.shading.wrap,
          nextStyle.clouds.shading.rimStrength,
        );
      } else {
        applyCloudShaderStyle(
          sharedMaterial,
          nextStyle.clouds.shading.cloudBaseHex,
          nextStyle.clouds.shading.wrap,
          nextStyle.clouds.shading.rimStrength,
        );
      }
      applyCloudFogStyle(sharedMaterial, nextStyle);

      for (const layer of layers) {
        const next = nextStyle.clouds.layers[layer.name];
        const prev = layer.lastApplied;
        const needsRebuild =
          next.enabled !== prev.enabled
          || next.count !== prev.count
          || next.altitude !== prev.altitude
          || next.domeRadius !== prev.domeRadius
          || next.coverage !== prev.coverage
          || next.scale !== prev.scale
          || next.shape !== prev.shape
          || next.width !== prev.width
          || next.height !== prev.height
          || next.elongation !== prev.elongation
          || next.edgeSoftness !== prev.edgeSoftness
          || next.noiseScale !== prev.noiseScale
          || next.noiseStrength !== prev.noiseStrength
          || next.opacity !== prev.opacity
          || next.normalStrength !== prev.normalStrength
          || next.curl !== prev.curl
          || next.windShear !== prev.windShear;
        layer.config = next;
        layer.lastApplied = snapshotLayer(next);
        if (needsRebuild) rebuildLayer(layer);
      }
    },
    update(dt) {
      for (const layer of layers) {
        layer.group.rotation.y += layer.config.rotateSpeed * dt;
      }
    },
    dispose() {
      for (const layer of layers) disposeLayerGroup(layer.group);
      sharedMaterial.dispose();
      root.clear();
    },
  };
}

const cloudHashNode = makeWgslFn(`
fn cloud_hash(p: vec2<f32>) -> f32 {
  var q = fract(p * vec2<f32>(123.34, 456.21));
  q = q + dot(q, q + vec2<f32>(45.32, 45.32));
  return fract(q.x * q.y);
}
`);

const cloudNoiseNode = makeWgslFn(`
fn cloud_noise(p: vec2<f32>) -> f32 {
  let i = floor(p);
  let f0 = fract(p);
  let u = f0 * f0 * (vec2<f32>(3.0, 3.0) - 2.0 * f0);
  return mix(
    mix(cloud_hash(i + vec2<f32>(0.0, 0.0)), cloud_hash(i + vec2<f32>(1.0, 0.0)), u.x),
    mix(cloud_hash(i + vec2<f32>(0.0, 1.0)), cloud_hash(i + vec2<f32>(1.0, 1.0)), u.x),
    u.y
  );
}
`, [cloudHashNode]);

const cloudFbmNode = makeWgslFn(`
fn cloud_fbm(p: vec2<f32>) -> f32 {
  return cloud_noise(p) * 0.58
    + cloud_noise(p * 2.03 + vec2<f32>(7.1, 7.1)) * 0.28
    + cloud_noise(p * 4.01 + vec2<f32>(13.7, 13.7)) * 0.14;
}
`, [cloudNoiseNode]);

const cloudEllipseAlphaNode = makeWgslFn(`
fn cloud_ellipse_alpha(q: vec2<f32>, radius: vec2<f32>, softness: f32) -> f32 {
  let d = length(q / radius);
  return 1.0 - smoothstep(1.0 - softness, 1.0, d);
}
`);

const cloudCardPositionNode = makeWgslFn(`
fn cloud_card_position(localPosition: vec3<f32>, texCoord: vec2<f32>, curl: f32, windShear: f32) -> vec3<f32> {
  let centered = texCoord * 2.0 - vec2<f32>(1.0, 1.0);
  var p = localPosition;
  p.y = p.y + sin(centered.x * 2.7) * curl * 4.0;
  p.y = p.y + centered.x * centered.x * windShear * 3.0;
  return p;
}
`);

const cloudRibbonAlphaNode = makeWgslFn(`
fn cloud_ribbon_alpha(q: vec2<f32>, breakup: f32, softness: f32, worldPos: vec3<f32>, curlInput: f32) -> f32 {
  let curl = sin(q.x * 4.2 + worldPos.x * 0.003) * 0.09 * curlInput;
  let spine = abs(q.y - curl);
  let endTaper = 1.0 - smoothstep(0.72, 1.0, abs(q.x));
  let band = 1.0 - smoothstep(0.08, 0.34 + softness * 0.18, spine);
  let split = smoothstep(0.18, 0.92, cloud_fbm(vec2<f32>(q.x * 1.7 + worldPos.z * 0.004, q.y * 5.0)));
  return band * endTaper * mix(1.0, split, breakup * 0.48);
}
`, [cloudFbmNode]);

const cloudWispAlphaNode = makeWgslFn(`
fn cloud_wisp_alpha(q: vec2<f32>, breakup: f32, softness: f32, worldPos: vec3<f32>, curlInput: f32, windShear: f32) -> f32 {
  let curl = sin(q.x * 3.1 + worldPos.z * 0.003) * 0.16 * curlInput;
  let spine = abs(q.y - curl + q.x * windShear * 0.08);
  let top = 1.0 - smoothstep(0.10, 0.45 + softness * 0.22, spine);
  let tail = 1.0 - smoothstep(0.42, 1.0, abs(q.x));
  let streaks = smoothstep(0.08, 0.85, cloud_fbm(vec2<f32>(q.x * 4.8 + worldPos.x * 0.004, q.y * 2.1)));
  return top * tail * mix(1.0, streaks, breakup * 0.62);
}
`, [cloudFbmNode]);

const cloudPuffAlphaNode = makeWgslFn(`
fn cloud_puff_alpha(q: vec2<f32>, breakup: f32, softness: f32, worldPos: vec3<f32>) -> f32 {
  var alpha = 0.0;
  alpha = max(alpha, cloud_ellipse_alpha(q - vec2<f32>(-0.42, -0.02), vec2<f32>(0.42, 0.42), softness));
  alpha = max(alpha, cloud_ellipse_alpha(q - vec2<f32>(-0.13, 0.12), vec2<f32>(0.48, 0.55), softness));
  alpha = max(alpha, cloud_ellipse_alpha(q - vec2<f32>(0.25, 0.04), vec2<f32>(0.46, 0.46), softness));
  alpha = max(alpha, cloud_ellipse_alpha(q - vec2<f32>(0.56, -0.08), vec2<f32>(0.34, 0.31), softness));
  alpha = alpha * (1.0 - smoothstep(0.64, 0.96, abs(q.x)));
  let underside = smoothstep(-0.92, -0.22, q.y);
  let breakupMask = smoothstep(0.12, 0.86, cloud_fbm(q * 2.4 + worldPos.xz * 0.004));
  return alpha * underside * mix(1.0, breakupMask, breakup * 0.32);
}
`, [cloudEllipseAlphaNode, cloudFbmNode]);

const cloudBankAlphaNode = makeWgslFn(`
fn cloud_bank_alpha(q: vec2<f32>, breakup: f32, softness: f32, worldPos: vec3<f32>) -> f32 {
  var base = 1.0 - smoothstep(0.24, 0.50 + softness * 0.24, abs(q.y + 0.24));
  base = base * (1.0 - smoothstep(0.72, 1.0, abs(q.x)));
  var lobes = 0.0;
  lobes = max(lobes, cloud_ellipse_alpha(q - vec2<f32>(-0.55, 0.03), vec2<f32>(0.36, 0.31), softness));
  lobes = max(lobes, cloud_ellipse_alpha(q - vec2<f32>(-0.18, 0.15), vec2<f32>(0.42, 0.39), softness));
  lobes = max(lobes, cloud_ellipse_alpha(q - vec2<f32>(0.24, 0.10), vec2<f32>(0.44, 0.34), softness));
  lobes = max(lobes, cloud_ellipse_alpha(q - vec2<f32>(0.62, -0.02), vec2<f32>(0.32, 0.25), softness));
  let streaks = smoothstep(0.08, 0.82, cloud_fbm(vec2<f32>(q.x * 2.9 + worldPos.z * 0.004, q.y * 3.8)));
  return max(base * 0.75, lobes) * mix(1.0, streaks, breakup * 0.42);
}
`, [cloudEllipseAlphaNode, cloudFbmNode]);

const cloudCardColorNode = makeWgslFn(`
fn cloud_card_color(
  texCoord: vec2<f32>,
  worldNormal: vec3<f32>,
  worldPos: vec3<f32>,
  cameraPos: vec3<f32>,
  shape: f32,
  edgeSoftness: f32,
  noiseScale: f32,
  noiseStrength: f32,
  opacity: f32,
  normalStrength: f32,
  curlInput: f32,
  windShear: f32,
  sunDir: vec3<f32>,
  sunColor: vec3<f32>,
  cloudZenithColor: vec3<f32>,
  cloudHorizonColor: vec3<f32>,
  cloudBase: vec3<f32>,
  wrapInput: f32,
  rimStrength: f32,
  cloudFogDensity: f32,
  sunDirWorld: vec3<f32>,
  horizonColor: vec3<f32>,
  zenithColor: vec3<f32>,
  fogWarmTint: vec3<f32>,
  fogCoolTint: vec3<f32>,
  inscatterStrength: f32,
  inscatterPower: f32,
  valueExposure: f32
) -> vec4<f32> {
  var q = texCoord * 2.0 - vec2<f32>(1.0, 1.0);
  q.y = q.y + q.x * q.x * windShear * 0.08;
  let breakup = clamp(noiseStrength, 0.0, 1.0);
  let softness = clamp(edgeSoftness, 0.02, 0.9);
  let edgeNoise = cloud_fbm(q * max(noiseScale, 0.1) + worldPos.xz * 0.005);
  q.y = q.y + (edgeNoise - 0.5) * breakup * 0.12;

  var alpha = cloud_ribbon_alpha(q, breakup, softness, worldPos, curlInput);
  if (shape > 0.5) {
    alpha = cloud_wisp_alpha(q, breakup, softness, worldPos, curlInput, windShear);
  }
  if (shape > 1.5) {
    alpha = cloud_puff_alpha(q, breakup, softness, worldPos);
  }
  if (shape > 2.5) {
    alpha = cloud_bank_alpha(q, breakup, softness, worldPos);
  }
  alpha = alpha * cloud_ellipse_alpha(q, vec2<f32>(1.04, 0.88), softness * 0.65);
  alpha = pow(clamp(alpha, 0.0, 1.0), 1.18);
  alpha = alpha * mix(1.0, smoothstep(0.03, 0.95, edgeNoise), breakup * 0.28);
  alpha = alpha * opacity;

  let N = normalize(worldNormal + vec3<f32>(-q.x * normalStrength * 0.2, (0.18 - q.y) * normalStrength, 0.0));
  let V = normalize(cameraPos - worldPos);
  let L = normalize(sunDir);
  let wrap = clamp((dot(N, L) + wrapInput) / (1.0 + wrapInput), 0.0, 1.0);
  let ndl = dot(N, L) * 0.5 + 0.5;
  let diff = mix(ndl, wrap, 0.5);
  let ambient = mix(cloudHorizonColor, cloudZenithColor, smoothstep(-0.3, 0.3, N.y));
  let silhouette = pow(1.0 - max(dot(N, V), 0.0), 3.0);
  let backlit = max(dot(-V, L), 0.0);
  let rim = silhouette * backlit * rimStrength;
  var col = cloudBase * (ambient + sunColor * diff) + sunColor * rim;
  let underside = smoothstep(-0.2, -0.6, N.y);
  col = col * mix(1.0, 0.7, underside);

  let viewVector = worldPos - cameraPos;
  let aerialViewDir = normalize(viewVector);
  let aerialDirFog = mix(horizonColor, zenithColor, smoothstep(-0.1, 0.6, aerialViewDir.y));
  let aerialSunFacing = pow(max(0.0, dot(aerialViewDir, normalize(sunDirWorld))), max(1.0, inscatterPower));
  let aerialInscatterTint = mix(fogCoolTint, fogWarmTint, aerialSunFacing);
  let aerialFogColor = mix(aerialDirFog, aerialInscatterTint, clamp(inscatterStrength * aerialSunFacing, 0.0, 1.0));
  let aerialDistance = length(viewVector);
  let aerialFogFactor = 1.0 - exp(-cloudFogDensity * cloudFogDensity * aerialDistance * aerialDistance);
  col = mix(col, aerialFogColor, aerialFogFactor);
  col = col * valueExposure;

  return vec4<f32>(col, alpha);
}
`, [
  cloudFbmNode,
  cloudEllipseAlphaNode,
  cloudRibbonAlphaNode,
  cloudWispAlphaNode,
  cloudPuffAlphaNode,
  cloudBankAlphaNode,
]);

function makeWebGPUCloudNodeMaterial(opts: CloudShaderMaterialOptions): MeshBasicNodeMaterial {
  const uniforms: WebGPUCloudUniforms = {
    uSunDir: makeNodeUniform(new THREE.Vector3(0.3, 0.7, 0.6).normalize()),
    uSunColor: makeNodeUniform(new THREE.Color('#ffffff')),
    uCloudZenithColor: makeNodeUniform(new THREE.Color('#ffffff')),
    uCloudHorizonColor: makeNodeUniform(new THREE.Color('#ffffff')),
    uCloudBase: makeNodeUniform(new THREE.Color(opts.cloudBaseHex)),
    uWrap: makeNodeUniform(opts.wrap),
    uRimStrength: makeNodeUniform(opts.rimStrength),
    uCloudFogDensity: makeNodeUniform(0),
    uSunDirWorld: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uSunDirWorld.value.clone()),
    uHorizonColor: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uHorizonColor.value.clone()),
    uZenithColor: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uZenithColor.value.clone()),
    uFogWarmTint: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uFogWarmTint.value.clone()),
    uFogCoolTint: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uFogCoolTint.value.clone()),
    uInscatterStrength: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uInscatterStrength.value),
    uInscatterPower: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uInscatterPower.value),
    uValueExposure: makeNodeUniform(valueExposureUniform('bg').uValueExposure.value),
  };
  const material = new MeshBasicNodeMaterial({
    name: 'cloud-card-webgpu-node-material',
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: true,
  });
  // Per-instance params packed into 2 vec4s by makeCloudCardGeometry to fit
  // WebGPU's 8 vertex-buffer cap. Layout:
  //   paramsA = (shape, edgeSoftness, noiseScale, noiseStrength)
  //   paramsB = (opacity, normalStrength, curl, windShear)
  const paramsA = makeNodeAttribute('iCloudParamsA', 'vec4');
  const paramsB = makeNodeAttribute('iCloudParamsB', 'vec4');
  material.positionNode = cloudCardPositionNode({
    localPosition: positionLocal,
    texCoord: makeNodeUv(),
    curl: paramsB.z,
    windShear: paramsB.w,
  });
  const cloud = cloudCardColorNode({
    texCoord: makeNodeUv(),
    worldNormal: normalWorld,
    worldPos: positionWorld,
    cameraPos: cameraPosition,
    shape: paramsA.x,
    edgeSoftness: paramsA.y,
    noiseScale: paramsA.z,
    noiseStrength: paramsA.w,
    opacity: paramsB.x,
    normalStrength: paramsB.y,
    curlInput: paramsB.z,
    windShear: paramsB.w,
    sunDir: uniforms.uSunDir,
    sunColor: uniforms.uSunColor,
    cloudZenithColor: uniforms.uCloudZenithColor,
    cloudHorizonColor: uniforms.uCloudHorizonColor,
    cloudBase: uniforms.uCloudBase,
    wrapInput: uniforms.uWrap,
    rimStrength: uniforms.uRimStrength,
    cloudFogDensity: uniforms.uCloudFogDensity,
    sunDirWorld: uniforms.uSunDirWorld,
    horizonColor: uniforms.uHorizonColor,
    zenithColor: uniforms.uZenithColor,
    fogWarmTint: uniforms.uFogWarmTint,
    fogCoolTint: uniforms.uFogCoolTint,
    inscatterStrength: uniforms.uInscatterStrength,
    inscatterPower: uniforms.uInscatterPower,
    valueExposure: uniforms.uValueExposure,
  });
  material.colorNode = cloud.rgb;
  material.opacityNode = cloud.a;
  material.userData.webgpuCloudUniforms = uniforms;
  return material;
}

export function makeCloudShaderMaterial(opts: CloudShaderMaterialOptions, rendererMode: RendererMode = 'webgl2'): CloudMaterial {
  if (rendererMode === 'webgpu') {
    return makeWebGPUCloudNodeMaterial(opts);
  }
  return new THREE.ShaderMaterial({
    name: 'cloud-card-shader',
    uniforms: {
      uSunDir: { value: new THREE.Vector3(0.3, 0.7, 0.6).normalize() },
      uSunColor: { value: new THREE.Color('#ffffff') },
      uSkyColor: { value: new THREE.Color('#ffffff') },
      uGroundColor: { value: new THREE.Color('#ffffff') },
      uCloudZenithColor: { value: new THREE.Color('#ffffff') },
      uCloudHorizonColor: { value: new THREE.Color('#ffffff') },
      uCloudBase: { value: new THREE.Color(opts.cloudBaseHex) },
      uWrap: { value: opts.wrap },
      uRimStrength: { value: opts.rimStrength },
      uCloudFogDensity: { value: 0 },
      ...SHARED_AERIAL_UNIFORMS,
      ...valueExposureUniform('bg'),
    },
    vertexShader: `
      // Per-instance params packed by makeCloudCardGeometry. See the WebGPU
      // makeWebGPUCloudNodeMaterial branch for the matching TSL reads.
      attribute vec4 iCloudParamsA;  // (shape, edgeSoftness, noiseScale, noiseStrength)
      attribute vec4 iCloudParamsB;  // (opacity, normalStrength, curl, windShear)
      varying vec2 vCloudUv;
      varying vec3 vNormalW;
      varying vec3 vWorldPos;
      varying float vCloudShape;
      varying float vCloudEdgeSoftness;
      varying float vCloudNoiseScale;
      varying float vCloudNoiseStrength;
      varying float vCloudOpacity;
      varying float vCloudNormalStrength;
      varying float vCloudCurl;
      varying float vCloudWindShear;

      void main() {
        vec2 centered = uv * 2.0 - 1.0;
        vec4 localPosition = vec4(position, 1.0);
        localPosition.y += sin(centered.x * 2.7) * iCloudParamsB.z * 4.0;
        localPosition.y += centered.x * centered.x * iCloudParamsB.w * 3.0;
        mat4 localModelMatrix = modelMatrix;

        #ifdef USE_INSTANCING
          localPosition = instanceMatrix * localPosition;
          localModelMatrix = modelMatrix * instanceMatrix;
        #endif

        vec4 worldPosition = modelMatrix * localPosition;
        vec4 mvPosition = viewMatrix * worldPosition;
        vCloudUv = uv;
        vNormalW = normalize(mat3(localModelMatrix) * normal);
        vWorldPos = worldPosition.xyz;
        vCloudShape = iCloudParamsA.x;
        vCloudEdgeSoftness = iCloudParamsA.y;
        vCloudNoiseScale = iCloudParamsA.z;
        vCloudNoiseStrength = iCloudParamsA.w;
        vCloudOpacity = iCloudParamsB.x;
        vCloudNormalStrength = iCloudParamsB.y;
        vCloudCurl = iCloudParamsB.z;
        vCloudWindShear = iCloudParamsB.w;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 uSunDir, uSunColor, uSkyColor, uGroundColor, uCloudZenithColor, uCloudHorizonColor, uCloudBase;
      uniform vec3 uSunDirWorld, uHorizonColor, uZenithColor, uFogWarmTint, uFogCoolTint;
      uniform float uWrap, uRimStrength, uInscatterStrength, uInscatterPower, uCloudFogDensity, uValueExposure;
      varying vec2 vCloudUv;
      varying vec3 vNormalW, vWorldPos;
      varying float vCloudShape, vCloudEdgeSoftness, vCloudNoiseScale, vCloudNoiseStrength, vCloudOpacity, vCloudNormalStrength, vCloudCurl, vCloudWindShear;

      float hash(vec2 p) {
        p = fract(p * vec2(123.34, 456.21));
        p += dot(p, p + 45.32);
        return fract(p.x * p.y);
      }

      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
          mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
          u.y
        );
      }

      float fbm(vec2 p) {
        float f = 0.0;
        f += noise(p) * 0.58;
        f += noise(p * 2.03 + 7.1) * 0.28;
        f += noise(p * 4.01 + 13.7) * 0.14;
        return f;
      }

      float ellipseAlpha(vec2 q, vec2 radius, float softness) {
        float d = length(q / radius);
        return 1.0 - smoothstep(1.0 - softness, 1.0, d);
      }

      float ribbonAlpha(vec2 q, float breakup, float softness) {
        float curl = sin(q.x * 4.2 + vWorldPos.x * 0.003) * 0.09 * vCloudCurl;
        float spine = abs(q.y - curl);
        float endTaper = 1.0 - smoothstep(0.72, 1.0, abs(q.x));
        float band = 1.0 - smoothstep(0.08, 0.34 + softness * 0.18, spine);
        float split = smoothstep(0.18, 0.92, fbm(vec2(q.x * 1.7 + vWorldPos.z * 0.004, q.y * 5.0)));
        return band * endTaper * mix(1.0, split, breakup * 0.48);
      }

      float wispAlpha(vec2 q, float breakup, float softness) {
        float curl = sin(q.x * 3.1 + vWorldPos.z * 0.003) * 0.16 * vCloudCurl;
        float spine = abs(q.y - curl + q.x * vCloudWindShear * 0.08);
        float top = 1.0 - smoothstep(0.10, 0.45 + softness * 0.22, spine);
        float tail = 1.0 - smoothstep(0.42, 1.0, abs(q.x));
        float streaks = smoothstep(0.08, 0.85, fbm(vec2(q.x * 4.8 + vWorldPos.x * 0.004, q.y * 2.1)));
        return top * tail * mix(1.0, streaks, breakup * 0.62);
      }

      float puffAlpha(vec2 q, float breakup, float softness) {
        float a = 0.0;
        a = max(a, ellipseAlpha(q - vec2(-0.42, -0.02), vec2(0.42, 0.42), softness));
        a = max(a, ellipseAlpha(q - vec2(-0.13, 0.12), vec2(0.48, 0.55), softness));
        a = max(a, ellipseAlpha(q - vec2(0.25, 0.04), vec2(0.46, 0.46), softness));
        a = max(a, ellipseAlpha(q - vec2(0.56, -0.08), vec2(0.34, 0.31), softness));
        a *= 1.0 - smoothstep(0.64, 0.96, abs(q.x));
        float underside = smoothstep(-0.92, -0.22, q.y);
        float breakupMask = smoothstep(0.12, 0.86, fbm(q * 2.4 + vWorldPos.xz * 0.004));
        return a * underside * mix(1.0, breakupMask, breakup * 0.32);
      }

      float bankAlpha(vec2 q, float breakup, float softness) {
        float base = 1.0 - smoothstep(0.24, 0.50 + softness * 0.24, abs(q.y + 0.24));
        base *= 1.0 - smoothstep(0.72, 1.0, abs(q.x));
        float lobes = 0.0;
        lobes = max(lobes, ellipseAlpha(q - vec2(-0.55, 0.03), vec2(0.36, 0.31), softness));
        lobes = max(lobes, ellipseAlpha(q - vec2(-0.18, 0.15), vec2(0.42, 0.39), softness));
        lobes = max(lobes, ellipseAlpha(q - vec2(0.24, 0.10), vec2(0.44, 0.34), softness));
        lobes = max(lobes, ellipseAlpha(q - vec2(0.62, -0.02), vec2(0.32, 0.25), softness));
        float streaks = smoothstep(0.08, 0.82, fbm(vec2(q.x * 2.9 + vWorldPos.z * 0.004, q.y * 3.8)));
        return max(base * 0.75, lobes) * mix(1.0, streaks, breakup * 0.42);
      }

      void main() {
        vec2 q = vCloudUv * 2.0 - 1.0;
        q.y += q.x * q.x * vCloudWindShear * 0.08;
        float breakup = clamp(vCloudNoiseStrength, 0.0, 1.0);
        float softness = clamp(vCloudEdgeSoftness, 0.02, 0.9);
        float edgeNoise = fbm(q * max(vCloudNoiseScale, 0.1) + vWorldPos.xz * 0.005);
        q.y += (edgeNoise - 0.5) * breakup * 0.12;

        float alpha = ribbonAlpha(q, breakup, softness);
        if (vCloudShape > 0.5) alpha = wispAlpha(q, breakup, softness);
        if (vCloudShape > 1.5) alpha = puffAlpha(q, breakup, softness);
        if (vCloudShape > 2.5) alpha = bankAlpha(q, breakup, softness);
        alpha *= ellipseAlpha(q, vec2(1.04, 0.88), softness * 0.65);
        alpha = pow(clamp(alpha, 0.0, 1.0), 1.18);
        alpha *= mix(1.0, smoothstep(0.03, 0.95, edgeNoise), breakup * 0.28);
        alpha *= vCloudOpacity;
        if (alpha < 0.01) discard;

        vec3 N = normalize(vNormalW + vec3(-q.x * vCloudNormalStrength * 0.2, (0.18 - q.y) * vCloudNormalStrength, 0.0));
        vec3 V = normalize(cameraPosition - vWorldPos);
        vec3 L = normalize(uSunDir);
        float wrap = clamp((dot(N, L) + uWrap) / (1.0 + uWrap), 0.0, 1.0);
        float ndl = dot(N, L) * 0.5 + 0.5;
        float diff = mix(ndl, wrap, 0.5);
        vec3 ambient = mix(uCloudHorizonColor, uCloudZenithColor, smoothstep(-0.3, 0.3, N.y));
        float silhouette = pow(1.0 - max(dot(N, V), 0.0), 3.0);
        float backlit = max(dot(-V, L), 0.0);
        float rim = silhouette * backlit * uRimStrength;
        vec3 col = uCloudBase * (ambient + uSunColor * diff) + uSunColor * rim;
        float underside = smoothstep(-0.2, -0.6, N.y);
        col *= mix(1.0, 0.7, underside);
        ${aerialPerspectiveFragmentExpression('vWorldPos - cameraPosition')}
        float aerialDistance = length(vWorldPos - cameraPosition);
        float aerialFogFactor = 1.0 - exp(-uCloudFogDensity * uCloudFogDensity * aerialDistance * aerialDistance);
        col = mix(col, aerialFogColor, aerialFogFactor);
        col *= uValueExposure;
        gl_FragColor = vec4(col, alpha);
      }
    `,
    depthWrite: false,
    transparent: true,
    side: THREE.DoubleSide,
    toneMapped: true,
  });
}

export function applyCloudUniforms(
  material: CloudMaterial,
  derived: DerivedLighting,
  baseHex: string,
  wrap: number,
  rimStrength: number,
): void {
  if (!(material instanceof THREE.ShaderMaterial)) {
    const uniforms = material.userData.webgpuCloudUniforms as WebGPUCloudUniforms;
    uniforms.uSunDir.value.copy(derived.sunDir).normalize();
    uniforms.uSunColor.value.set(derived.sunColorHex).multiplyScalar(derived.sunIntensity);
    uniforms.uCloudZenithColor.value.set(derived.hemiSkyHex).multiplyScalar(derived.hemiIntensity);
    uniforms.uCloudHorizonColor.value.set(derived.hemiGroundHex).multiplyScalar(derived.hemiIntensity);
    applyCloudShaderStyle(material, baseHex, wrap, rimStrength);
    return;
  }
  material.uniforms.uSunDir.value.copy(derived.sunDir).normalize();
  material.uniforms.uSunColor.value.set(derived.sunColorHex).multiplyScalar(derived.sunIntensity);
  material.uniforms.uSkyColor.value.set(derived.hemiSkyHex).multiplyScalar(derived.hemiIntensity);
  material.uniforms.uGroundColor.value.set(derived.hemiGroundHex).multiplyScalar(derived.hemiIntensity);
  material.uniforms.uCloudZenithColor.value.set(derived.hemiSkyHex).multiplyScalar(derived.hemiIntensity);
  material.uniforms.uCloudHorizonColor.value.set(derived.hemiGroundHex).multiplyScalar(derived.hemiIntensity);
  applyCloudShaderStyle(material, baseHex, wrap, rimStrength);
}

function applyCloudShaderStyle(
  material: CloudMaterial,
  baseHex: string,
  wrap: number,
  rimStrength: number,
): void {
  if (!(material instanceof THREE.ShaderMaterial)) {
    const uniforms = material.userData.webgpuCloudUniforms as WebGPUCloudUniforms;
    uniforms.uCloudBase.value.set(baseHex);
    uniforms.uWrap.value = wrap;
    uniforms.uRimStrength.value = rimStrength;
    return;
  }
  material.uniforms.uCloudBase.value.set(baseHex);
  material.uniforms.uWrap.value = wrap;
  material.uniforms.uRimStrength.value = rimStrength;
}

function applyCloudFogStyle(material: CloudMaterial, style: StyleConfig): void {
  const fogOn = LAYER_NAMES.some((name) => style.clouds.layers[name].enabled && style.clouds.layers[name].fogEnabled);
  const density = fogOn ? style.fog.density : 0;
  if (!(material instanceof THREE.ShaderMaterial)) {
    const uniforms = material.userData.webgpuCloudUniforms as WebGPUCloudUniforms;
    uniforms.uCloudFogDensity.value = density;
    uniforms.uSunDirWorld.value.copy(SHARED_AERIAL_UNIFORMS.uSunDirWorld.value).normalize();
    uniforms.uHorizonColor.value.copy(SHARED_AERIAL_UNIFORMS.uHorizonColor.value);
    uniforms.uZenithColor.value.copy(SHARED_AERIAL_UNIFORMS.uZenithColor.value);
    uniforms.uFogWarmTint.value.copy(SHARED_AERIAL_UNIFORMS.uFogWarmTint.value);
    uniforms.uFogCoolTint.value.copy(SHARED_AERIAL_UNIFORMS.uFogCoolTint.value);
    uniforms.uInscatterStrength.value = SHARED_AERIAL_UNIFORMS.uInscatterStrength.value;
    uniforms.uInscatterPower.value = SHARED_AERIAL_UNIFORMS.uInscatterPower.value;
    uniforms.uValueExposure.value = valueExposureUniform('bg').uValueExposure.value;
    return;
  }
  // Diff before flagging needsUpdate. Setting needsUpdate=true forces a
  // shader recompile, which is a synchronous frame-time stall — we don't
  // want that on every ScenePanel slider tweak when the fog flag itself
  // hasn't actually changed.
  if (material.fog !== fogOn) {
    material.fog = fogOn;
    material.needsUpdate = true;
  }
  if (material instanceof THREE.ShaderMaterial) material.uniforms.uCloudFogDensity.value = density;
}

function rebuildLayer(layer: LayerHandle): void {
  disposeLayerGroup(layer.group);
  layer.group.visible = layer.config.enabled;

  const instanceCount = layer.config.enabled && layer.config.count > 0 ? Math.round(layer.config.count) : 0;
  const geometry = makeCloudCardGeometry(layer.config, instanceCount);
  const mesh = new THREE.InstancedMesh(geometry, layer.material, instanceCount);
  mesh.name = `cloud-cards-${layer.name}`;
  mesh.userData.cloudLayer = layer.name;
  tagValueBucket(mesh, 'bg');
  mesh.renderOrder = -450;
  mesh.updateMatrix();
  mesh.matrixAutoUpdate = false;
  // frustumCulled stays false — the cloud root is fixed at world origin
  // while the camera moves through the canyon, so the InstancedMesh's
  // bounding sphere becomes meaningless once the camera is far from origin.
  // Re-enabling would cause clouds to vanish at certain camera positions.
  mesh.frustumCulled = false;
  mesh.count = instanceCount;

  const seedOffset = layer.name === 'low' ? 7 : layer.name === 'mid' ? 0 : 13;
  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const rotation = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const euler = new THREE.Euler();

  for (let i = 0; i < instanceCount; i++) {
    const t = (i + 0.5) / instanceCount;
    const angle = t * Math.PI * 2 * 2.399963 + pseudo(i + seedOffset, 4) * 0.55;
    const radius = layer.config.domeRadius * THREE.MathUtils.lerp(0.58, 1.0, pseudo(i + seedOffset, 13));
    const verticalLift = THREE.MathUtils.lerp(-18, 88, pseudo(i + seedOffset, 17)) * layer.config.coverage;
    position.set(
      Math.cos(angle) * radius,
      layer.config.altitude + verticalLift,
      Math.sin(angle) * radius,
    );
    euler.set(
      THREE.MathUtils.degToRad(THREE.MathUtils.lerp(-4, 5, pseudo(i + seedOffset, 37))),
      angle - Math.PI * 0.5,
      THREE.MathUtils.degToRad(THREE.MathUtils.lerp(-5, 5, pseudo(i + seedOffset, 41))),
    );
    rotation.setFromEuler(euler);
    const localScale = layer.config.scale * THREE.MathUtils.lerp(0.75, 1.25, pseudo(i + seedOffset, 43));
    scale.set(localScale, localScale, localScale);
    matrix.compose(position, rotation, scale);
    mesh.setMatrixAt(i, matrix);
  }

  mesh.instanceMatrix.needsUpdate = true;
  layer.group.add(mesh);
}

function makeCloudCardGeometry(layer: CloudLayerConfig, instanceCount: number): THREE.BufferGeometry {
  const geometry = new THREE.PlaneGeometry(layer.width * layer.elongation, layer.height, 28, 8);
  // Pack the 8 per-instance floats into 2 vec4s so the cloud material fits
  // under WebGPU's 8 vertex-buffer limit. The previous shape (8 separate
  // instanced floats + 8 duplicated vertex floats) declared 11 attributes,
  // failing pipeline creation with "Vertex buffer count exceeds maximum".
  // Layout — keep in sync with the WGSL/TSL reads in makeWebGPUCloudNodeMaterial
  // and the WebGL vertex shader in makeCloudShaderMaterial:
  //   iCloudParamsA = (shape, edgeSoftness, noiseScale, noiseStrength)
  //   iCloudParamsB = (opacity, normalStrength, curl, windShear)
  const paramsA = new Float32Array(instanceCount * 4);
  const paramsB = new Float32Array(instanceCount * 4);
  for (let i = 0; i < instanceCount; i++) {
    const a = i * 4;
    paramsA[a + 0] = SHAPE_CODES[layer.shape];
    paramsA[a + 1] = layer.edgeSoftness;
    paramsA[a + 2] = layer.noiseScale;
    paramsA[a + 3] = layer.noiseStrength;
    paramsB[a + 0] = layer.opacity;
    paramsB[a + 1] = layer.normalStrength;
    paramsB[a + 2] = layer.curl;
    paramsB[a + 3] = layer.windShear;
  }
  geometry.setAttribute('iCloudParamsA', new THREE.InstancedBufferAttribute(paramsA, 4));
  geometry.setAttribute('iCloudParamsB', new THREE.InstancedBufferAttribute(paramsB, 4));
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  return geometry;
}

function pseudo(a: number, b: number): number {
  let n = Math.floor(a * 1000) * 374761393 + Math.floor(b) * 668265263;
  n = (n ^ (n >>> 13)) * 1274126177;
  n = n ^ (n >>> 16);
  return ((n >>> 0) % 10000) / 10000;
}

function disposeLayerGroup(group: THREE.Group): void {
  for (const child of group.children) {
    child.traverse((object) => {
      if (object instanceof THREE.Mesh) object.geometry.dispose();
    });
  }
  group.clear();
}
